import './assets/main.css'

import { createApp } from 'vue'
import { createPinia } from 'pinia'
import * as Sentry from '@sentry/vue'

import App from './App.vue'
import { FontAwesomeIcon } from './plugins/fontawesome'
import { initializeMqttConnection } from './composables/useMqttConnection'
import { useTranslationsStore } from './stores/translations'
import { TRUSTED_PARENT_ORIGINS } from './constants/trusted-origins'

const app = createApp(App)
const pinia = createPinia()
pinia.use(Sentry.createSentryPiniaPlugin())

const sentryDsn =
  import.meta.env.VITE_SENTRY_DSN ||
  'https://981ca539c628a1ee4d735c5ba35356cf@o4510809510051840.ingest.de.sentry.io/4510809803915344'
const sentryTunnel = import.meta.env.VITE_SENTRY_TUNNEL || '/tunnel/4510809803915344'

Sentry.init({
  app,
  dsn: sentryDsn,
  environment: import.meta.env.VITE_APP_ENV || import.meta.env.MODE,
  tunnel: sentryTunnel,
  sendDefaultPii: true,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
    Sentry.vueIntegration({
      tracingOptions: {
        trackComponents: true,
        hooks: ['activate', 'mount', 'update', 'unmount'],
      },
    }),
  ],
  normalizeDepth: 10,
  tracesSampleRate: 1.0,
  tracePropagationTargets: ['localhost', /^https:\/\/api\.dinoconnect\.de/],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  enableLogs: true,
})

app.use(pinia)

// Load translations after Pinia is initialized
const translationsStore = useTranslationsStore()

// Get language from URL parameter, default to 'de'
const urlParams = new URLSearchParams(window.location.search)
const language = urlParams.get('lang') || urlParams.get('language') || 'de'
const sleepSeconds = urlParams.get('sleep')
if (import.meta.env.DEV) {
  console.log('🌐 Loading translations for language:', language)
  if (sleepSeconds) {
    console.log('😴 Sleep parameter detected:', sleepSeconds, 'seconds')
  }
}

// Update HTML lang attribute for accessibility and SEO
document.documentElement.lang = language
if (import.meta.env.DEV) {
  console.log('📄 HTML lang attribute set to:', language)
}

translationsStore.loadTranslations(language)

app.component('font-awesome-icon', FontAwesomeIcon)
app.mount('#yellow-dinoremote-app')

// Define trusted origins for postMessage
const TRUSTED_ORIGINS = TRUSTED_PARENT_ORIGINS

const getParentOrigin = (): string | null => {
  if (!document.referrer) {
    return null
  }
  try {
    return new URL(document.referrer).origin
  } catch {
    return null
  }
}

// Add event listeners to send size to parent
window.addEventListener('load', sendHeightToParent)

let lastHeight = 0

function sendHeightToParent() {
  const height = document.documentElement.scrollHeight || document.body.scrollHeight

  // Only send message if height has actually changed
  if (height !== lastHeight) {
    // Only send to parent if we're in an iframe
    if (window.parent !== window) {
      const parentOrigin = getParentOrigin()
      if (parentOrigin && TRUSTED_ORIGINS.includes(parentOrigin)) {
        window.parent.postMessage({ height }, parentOrigin)
      }
    }
    lastHeight = height
  }
}

const resizeObserver = new ResizeObserver(() => {
  sendHeightToParent()
})

// Start observing the document's body for size changes
resizeObserver.observe(document.body)

// Remember to disconnect the observer when it's no longer needed
window.addEventListener('beforeunload', () => {
  resizeObserver.disconnect()
})

// Sleep utility function
const sleep = (seconds: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, seconds * 1000))
}

// Securely expose the initializeMqttConnection function to the window object
// Define a type for the window object with our custom properties
declare global {
  interface Window {
    initializeMqttConnection?: (token: string, deviceId: string, sessionId: string) => void
    sleepSeconds?: string | null
  }
}

window.initializeMqttConnection = (token: string, deviceId: string, sessionId: string) => {
  // Additional security check
  if (
    !token ||
    !deviceId ||
    !sessionId ||
    typeof token !== 'string' ||
    typeof deviceId !== 'string' ||
    typeof sessionId !== 'string'
  ) {
    console.error('Invalid parameters provided to initializeMqttConnection')
    return
  }

  initializeMqttConnection(token, deviceId, sessionId)
}

// Expose sleep parameter globally
window.sleepSeconds = sleepSeconds

// Development helper for local testing
// This simulates receiving auth data from a parent window
if (import.meta.env.DEV) {
  // Load auth data from environment variables if available
  const devAccessToken = import.meta.env.VITE_DEV_ACCESS_TOKEN
  const devDeviceIdentifier = import.meta.env.VITE_DEV_DEVICE_IDENTIFIER
  const devSessionId = import.meta.env.VITE_DEV_SESSION_ID

  if (devAccessToken && devDeviceIdentifier && devSessionId) {
    console.info('Loading auth data from environment variables')

    // Simulate a postMessage from parent with sleep delay if specified
    const simulateAuthData = async () => {
      if (sleepSeconds) {
        const sleepDuration = parseInt(sleepSeconds, 10)
        if (!isNaN(sleepDuration) && sleepDuration > 0) {
          console.log(
            `😴 Development mode: Sleeping for ${sleepDuration} seconds before simulating auth data...`,
          )
          await sleep(sleepDuration)
          console.log('😴 Development mode: Sleep completed, simulating auth data')
        }
      }

      window.postMessage(
        {
          type: 'AUTH_DATA',
          access_token: devAccessToken as string,
          device_identifier: devDeviceIdentifier as string,
          session_id: devSessionId as string,
        },
        window.location.origin,
      )
    }

    simulateAuthData()
  } else {
    console.info(
      'Development mode: Add authentication data to your .env.local file:\n' +
        'VITE_DEV_ACCESS_TOKEN=your-access-token\n' +
        'VITE_DEV_DEVICE_IDENTIFIER=your-device-identifier\n' +
        'VITE_DEV_SESSION_ID=your-session-id\n\n' +
        'URL parameters:\n' +
        '- Language: ?lang=en or ?language=en\n' +
        '- Sleep delay: ?sleep=5 (sleep for 5 seconds after auth params received)',
    )
  }
}
